import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from "react";
import {
  setTokens,
  clearTokens,
  getAccessToken,
  getRefreshToken,
  getTokenExpiryTime
} from "../utils/tokenUtils";

const AuthContext = createContext({});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    const storedUser = localStorage.getItem("user");
    return storedUser ? JSON.parse(storedUser) : null;
  });
  const [loading, setLoading] = useState(true);

  // Use ref for timer to avoid stale closures
  const refreshTimerRef = useRef(null);

  /**
   * Setup proactive token refresh timer
   */
  const setupRefreshTimer = useCallback(() => {
    // Clear any existing timer
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    const timeUntilExpiry = getTokenExpiryTime();

    // If token expires in the future, setup refresh timer
    // Refresh 60 seconds before expiry
    if (timeUntilExpiry > 0) {
      const refreshTime = Math.max(timeUntilExpiry - 60000, 0);

      refreshTimerRef.current = setTimeout(async () => {
        // The axios interceptor will handle the actual refresh
        // This timer triggers a profile fetch which invokes the interceptor
        try {
          const { authService } = await import('../services/api');
          await authService.profile();
          // Setup next timer after successful refresh
          setupRefreshTimer();
        } catch (error) {
          console.error('Proactive refresh failed:', error);
        }
      }, refreshTime);
    }
  }, []);

  useEffect(() => {
    // Initialize - check for existing valid session
    const token = getAccessToken();
    if (token && user) {
      setupRefreshTimer();
    }
    setLoading(false);

    // Cleanup timer on unmount
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, [user, setupRefreshTimer]);

  useEffect(() => {
    // Keep localStorage in sync with user state
    if (user) {
      localStorage.setItem("user", JSON.stringify(user));
    } else {
      localStorage.removeItem("user");
    }
  }, [user]);

  /**
   * Login - stores user and tokens
   * @param {Object} userData - { user, token, refreshToken } or { user, accessToken, refreshToken }
   */
  const login = (userData) => {
    setUser(userData.user);
    localStorage.setItem("user", JSON.stringify(userData.user));

    // Support both 'token' and 'accessToken' naming
    const accessToken = userData.token || userData.accessToken;
    const refreshToken = userData.refreshToken;

    setTokens(accessToken, refreshToken);

    // Setup proactive refresh
    setupRefreshTimer();
  };

  /**
   * Logout - clears all auth state
   */
  const logout = async () => {
    // Clear refresh timer
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    // Optionally notify backend (non-blocking)
    const refreshToken = getRefreshToken();
    if (refreshToken) {
      try {
        const { logoutApi } = await import('../services/auth.service');
        await logoutApi(refreshToken);
      } catch (error) {
        // Ignore - we're logging out anyway
      }
    }

    // Clear local state
    setUser(null);
    clearTokens();
  };

  /**
   * Update user profile data
   */
  const updateUser = (updatedUserData) => {
    setUser((prevUser) => {
      const mergedUser = { ...prevUser, ...updatedUserData };
      localStorage.setItem("user", JSON.stringify(mergedUser));
      return mergedUser;
    });
  };

  /**
   * Update tokens (useful after refresh or re-authentication)
   */
  const updateTokens = (accessToken, refreshToken) => {
    setTokens(accessToken, refreshToken);
    setupRefreshTimer();
  };

  return (
    <AuthContext.Provider value={{
      user,
      login,
      logout,
      updateUser,
      updateTokens,
      loading
    }}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
